import DataLoader from 'dataloader'
import { filterNonNil } from 'utils/rx-util'
import { IAMAPI } from './API'
import User from './models/User'
import { collection } from 'infra/collection'
import { UserDTO } from './dtos/user'
import { PrimeProps, Id } from 'types/definitions'
import { Observable } from 'rxjs'
import { RefreshOnlineStatusStream } from './RefreshOnlineStatusStream'
import { beingObservedStream, fromStream } from 'utils/mobx-util'
import { action, computed, runInAction, makeObservable } from 'mobx'
import { switchMap, startWith, distinctUntilChanged, map } from 'rxjs/operators'
import { SessionInfo } from 'auth/AuthStore'

export type PrimeUser = (userDto: PrimeProps<UserDTO, 'public_id'>) => User

export default class UserStore {
  /**
   * Data loader for users.
   *
   * @private
   */
  private readonly loader = new DataLoader(this.fetchManyInternal.bind(this))

  /**
   * Users.
   */
  users = collection<UserDTO, User>({
    create: () => {
      const user = new User(() => this.currentUser?.id ?? null)
      this.subscribeToOnlineStatus(user)
      return user
    },
    getDataId: (data) => data.public_id,
    getModelId: (model) => model.publicId,
  })

  /**
   * Current logged in user. May not be available as fast as currentUserId
   * since it requires a call to IAM.
   */
  @computed
  get currentUser(): User | null {
    return this.$.currentUser() ?? null
  }

  /**
   * Primes the user in the store and emits the primed user whenever the session changes.
   */
  primeUser$ = this.session$.pipe(
    filterNonNil(),
    map((x) => x.authId),
    // The user was primed in the AuthStore so when this happens we can find it by userId
    map((authId) => this.users.find((x) => x.authId === authId))
  )

  /**
   * Emits when we have the current user.
   */
  currentUser$ = this.primeUser$

  private readonly $ = {
    currentUser: fromStream(null, this.currentUser$),
  }

  constructor(
    private api: IAMAPI,
    private getStatusChangeStreamForUser: (user: User) => Observable<any>,
    private fetchOnlineStatus: (user: User) => Promise<boolean>,
    private session$: Observable<SessionInfo | null>
  ) {
    makeObservable(this)
  }

  async fetch(id: Id) {
    return this.loader.load(Id.assert(id))
  }

  primeUser(props: PrimeProps<UserDTO, 'public_id'>) {
    if (!props?.public_id) {
      return undefined
    }

    const user = this.users.set({
      public_id: props.public_id,
      ...(props.user_id && {
        user_id: props.user_id,
      }),
    })

    this.fetch(props.public_id).then()

    if (user.isFullyLoaded) {
      // The user has already been hydrated from the server, so don't
      // assign any props to it
      return user
    }

    return user.set(props, {
      stripUndefined: true,
      parse: true,
    })
  }

  /**
   * Batching implementation for getting users.
   *
   * @private
   * @param userPublicIds
   */
  private async fetchManyInternal(
    userPublicIds: readonly string[]
  ): Promise<Array<User | null>> {
    const response = await this.api.users.getManyByPublicId(userPublicIds)
    // Mark loaded users.
    return runInAction(() => {
      const users = this.users.set(response)
      users.forEach((user) => {
        if (user) {
          user.markFullyLoaded()
        }
      })
      return users
    })
  }

  /**
   * Syncs the online status whenever the `online` observable becomes observed, or when
   * notified of a status change (but only if the `online` observable has already been observed at least once)
   *
   * @param user
   */
  private subscribeToOnlineStatus(user: User) {
    RefreshOnlineStatusStream(
      beingObservedStream(user, 'online'),
      this.getStatusChangeStreamForUser(user)
    )
      .pipe(
        // The current user is always online.
        switchMap(
          async () => user.isCurrentUser || this.fetchOnlineStatus(user)
        ),
        startWith(false),
        distinctUntilChanged()
      )
      .subscribe(
        action((online: boolean) => {
          user.online = online
        })
      )
  }
}
